import {
  type AddOnOutput,
  AddOnSelectionConditionPayloadType,
  type HighFrequency_AddOn,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

const isAddonOutput = (addon: AddOnOutput | HighFrequency_AddOn | string): boolean =>
  !!(addon as AddOnOutput).name && !!(addon as AddOnOutput).value

export const getNormalizedAddOns = (
  addOns: (AddOnOutput | HighFrequency_AddOn | string)[],
): string[] =>
  addOns.map((addOn) => (isAddonOutput(addOn) ? (addOn as AddOnOutput).name : (addOn as string)))

export type AddOnRequirements = Record<string, string>

type NamedSelectableAddOn = SelectableAddOn & { name: string }

type SelectedAddOn = {
  name: string
  value: string
}

type SelectionCondition = {
  addOnName?: Maybe<string>
  type?: Maybe<AddOnSelectionConditionPayloadType>
}

type SelectableAddOn = {
  selectableWhen?: Maybe<SelectionCondition>
}

export const isSameAddOn = (name: string, otherName: string) =>
  name.toLowerCase() === otherName.toLowerCase()

export const getRequiredAddOnName = (addOn?: Maybe<SelectableAddOn>): Maybe<string> => {
  const condition = addOn?.selectableWhen
  if (condition?.type !== AddOnSelectionConditionPayloadType.OtherAddonSelected) return null

  return condition.addOnName ?? null
}

export const getAddOnRequirements = (addOns: NamedSelectableAddOn[]): AddOnRequirements =>
  addOns.reduce<AddOnRequirements>((requirements, addOn) => {
    const requiredAddOn = getRequiredAddOnName(addOn)
    if (requiredAddOn) requirements[addOn.name.toLowerCase()] = requiredAddOn

    return requirements
  }, {})

export const getRequiredAddOn = (requirements: AddOnRequirements, name: string): Maybe<string> =>
  requirements[name.toLowerCase()] ?? null

export const hasSelectedAddOn = (selectedAddOns: SelectedAddOn[], name: string) =>
  !!selectedAddOns.find((addOn) => isSameAddOn(addOn.name, name))?.value

export const isAddOnRestricted = (
  requirements: AddOnRequirements,
  selectedAddOns: SelectedAddOn[],
  name: string,
) => {
  const requiredAddOn = getRequiredAddOn(requirements, name)

  return !!requiredAddOn && !hasSelectedAddOn(selectedAddOns, requiredAddOn)
}

export const sanitizeSelectedAddOns = <T extends SelectedAddOn>(
  requirements: AddOnRequirements,
  selectedAddOns: T[],
): T[] =>
  selectedAddOns.map((addOn) =>
    addOn.value && isAddOnRestricted(requirements, selectedAddOns, addOn.name)
      ? { ...addOn, value: '' }
      : addOn,
  )
